// shortcutOverridesContext — ショートカット override 差分の **プロセス内 single source
// of truth** + pub/sub (shortcut-settings.md §2, スライス2)。
//
// なぜ Context ではなく module-singleton + useSyncExternalStore か:
//   - 配線フック (`useLayoutKeyboardShortcuts`) は Classic/Modern の **深い場所**で
//     呼ばれ、設定パネル (`ShortcutSettingsPanel`) は別 subtree (モーダル) にある。
//     両者を 1 つの React Context Provider で囲うと layout shell の改修が広がる。
//   - override は **端末ローカル localStorage の薄いラッパ**なので、グローバル singleton
//     にして `useSyncExternalStore` で購読する方が prop-drilling / Provider 追加より軽い。
//   - パネルが setOne すると singleton が listener へ通知 → 配線フックも設定パネルも
//     同 snapshot で再 render / 再 subscribe される (= リバインドが即時反映)。
//
// localStorage の実体は `shortcutOverridesStore.ts` (storage-injection 可能・テスト済)。
// ここは「1 インスタンスを共有 + 変更通知」だけを足す薄い層。

import { useSyncExternalStore } from 'react';

import {
  createShortcutOverridesStore,
  type ShortcutOverrides,
} from '../../infrastructure/shortcutOverridesStore.js';

// プロセス内で 1 つだけ。browser では localStorage を掴み、SSR/test では no-op に落ちる。
const store = createShortcutOverridesStore();

// 現 snapshot を cache する (= useSyncExternalStore は getSnapshot の参照同一性を要求)。
// localStorage 読みは毎回新 object を返すので、setter 経由でのみ snapshot を作り直す。
let snapshot: ShortcutOverrides = store.getAll();

const listeners = new Set<() => void>();

const emit = (): void => {
  snapshot = store.getAll();
  for (const l of listeners) l();
};

/** useSyncExternalStore 用 subscribe。listener は override 変更時に呼ばれる。 */
export const subscribeShortcutOverrides = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/** useSyncExternalStore 用 getSnapshot。参照は setter 経由でのみ変わる。 */
export const getShortcutOverridesSnapshot = (): ShortcutOverrides => snapshot;

/** SSR getServerSnapshot (= 常に空。サーバ同期しないので localStorage は無い)。 */
const EMPTY: ShortcutOverrides = {};
const getServerSnapshot = (): ShortcutOverrides => EMPTY;

/** 1 action の override を設定して購読者へ通知する (= リバインド確定)。 */
export const setShortcutOverride = (actionId: string, keys: readonly string[]): void => {
  store.setOne(actionId, keys);
  emit();
};

/** 1 action の override を削除して通知する (= 既定へ戻す per-row reset)。 */
export const resetShortcutOverride = (actionId: string): void => {
  store.resetOne(actionId);
  emit();
};

/** 全 override を削除して通知する (= 全 reset)。 */
export const resetAllShortcutOverrides = (): void => {
  store.resetAll();
  emit();
};

/**
 * React からの購読 hook。override 変更で再 render される。配線フック / 設定パネルの
 * 両方がこれを使い、同 snapshot を共有する。
 */
export const useShortcutOverrides = (): ShortcutOverrides =>
  useSyncExternalStore(subscribeShortcutOverrides, getShortcutOverridesSnapshot, getServerSnapshot);
